/**
 * The Memory step (Ram, 2026-10-27) — a Do step for the heap's three jobs,
 * shown as its own step, in words:
 *
 *   Get memory        for [count] [whole numbers]  [ ] Set every value to 0 first  keep it in [p]
 *   Resize memory     [p]  to hold [total] whole numbers
 *   Give memory back  [p]
 *
 * The step writes the sizeof for you: "memory for n whole numbers" is
 * `malloc(n * sizeof(int))`, "every value 0" is `calloc(n, sizeof(int))`.
 *
 * Storage is Do's (an 'action', `actionData.expression` + `assignTo`), so
 * codegen, Code Next and every existing item are unchanged; `stepKind:
 * 'memory'` marks a step made as one. The old guided forms' fields (`kind:
 * 'allocate' | 'resize' | 'free'` with `alloc` / `resize` / `free`) are written
 * too, so `?inputEditor=old` still opens the guided form. Pure: no React, no store.
 */

export const MEMORY_FNS = ['malloc', 'calloc', 'realloc', 'free'];
const MEMORY_KINDS = ['allocate', 'resize', 'free'];

/** The + menu / panel header words. */
export const MEMORY_STEP_WORDS = { label: 'Memory', description: 'Get memory, resize it, give it back' };

/** The three jobs, in the order a block is used. */
export const MEMORY_JOBS = [
  { job: 'get', label: 'Get memory' },
  { job: 'resize', label: 'Resize memory' },
  { job: 'free', label: 'Give memory back' },
];

// ── types ────────────────────────────────────────────────────────────────────

const tidy = (t) => String(t || '').trim().replace(/\s+/g, ' ');

/** `struct_Node` / `struct  Node` → `struct Node`; others tidied. */
export const cTypeOf = (t) => tidy(t).replace(/^(struct|union)[_\s]+/, '$1 ');

/**
 * What a pointer resource points at, as C (`int`, `struct Node`), or ''.
 * Three stored shapes: `int*`; `struct_Node*` + pointsToType; and a pointer
 * whose dataType is already the pointee (`int`, pointerLevel 1).
 */
export function pointeeOf(r) {
  if (!r) return '';
  const pt = tidy(r.pointsToType);
  if (pt) return cTypeOf(pt.replace(/\*$/, ''));
  const dt = tidy(r.dataType);
  if (/\*\s*\*$/.test(dt)) return '';                // a pointer to a pointer: not a block of values
  if (/\*$/.test(dt)) return cTypeOf(dt.replace(/\s*\*$/, ''));
  const rt = String(r.resourceType || r.type || '').toLowerCase();
  return rt === 'pointer' ? cTypeOf(dt) : '';
}

/** A pointer a block can be kept in: one level, not a file, not a function. */
export function isBlockPointer(r) {
  if (!r || !r.name) return false;
  const rt = String(r.resourceType || r.type || '').toLowerCase();
  const dt = tidy(r.dataType);
  if (rt === 'function_pointer' || /\bFILE\b/.test(dt) || /\bFILE\b/.test(tidy(r.pointsToType))) return false;
  if (Number(r.pointerLevel) > 1) return false;
  const p = pointeeOf(r);
  return !!p && p !== 'void' && (rt === 'pointer' || /\*$/.test(dt));
}

/** The pointer's C type for a new resource holding `type` (struct as stored: `struct_Node*`). */
export const pointerTypeFor = (type) => {
  const t = cTypeOf(type);
  const m = /^(struct|union) (\w+)$/.exec(t);
  return m ? `${m[1]}_${m[2]}*` : `${t}*`;
};

/** "whole numbers" / "a whole number"-free count words for a C type. */
export function kindWords(type, many = true) {
  const t = cTypeOf(type);
  const rec = /^(?:struct|union) (\w+)$/.exec(t);
  if (rec) return `${rec[1]} record${many ? 's' : ''}`;
  if (/^(unsigned )?(int|long|short|long long|size_t)$/.test(t)) return many ? 'whole numbers' : 'whole number';
  if (/^(float|double|long double)$/.test(t)) return many ? 'decimals' : 'decimal';
  if (t === 'char') return many ? 'characters' : 'character';
  if (t === 'bool' || t === '_Bool') return many ? 'yes/no values' : 'yes/no value';
  return many ? `${t} values` : `${t} value`;
}

/** The kinds a new pointer can be made for: the basics, then the record types in scope. */
export function kindChoices(resources = []) {
  const out = [['int', 'whole numbers'], ['float', 'decimals'], ['char', 'characters']];
  (resources || []).forEach((r) => {
    const rt = String((r && (r.resourceType || r.type)) || '').toLowerCase();
    if ((rt === 'struct_type' || rt === 'struct_definition') && r.name) {
      const t = `struct ${r.name}`;
      if (!out.some(([c]) => c === t)) out.push([t, kindWords(t)]);
    }
  });
  return out;
}

// ── parse / compose ──────────────────────────────────────────────────────────

const CAST_RE = /^\(\s*((?:struct|union)?\s*\w+(?:\s+\w+)*)\s*\*\s*\)\s*/;
const SIZEOF_RE = /^sizeof\s*\(\s*((?:struct\s+|union\s+)?\w+(?:\s+\w+)*)\s*\)$/;

/** Split `a * b` at its LAST top-level `*` (so `n * 2 * sizeof(int)` → `n * 2`, `sizeof(int)`). */
function splitTimes(s) {
  let depth = 0;
  for (let i = s.length - 1; i >= 0; i -= 1) {
    const ch = s[i];
    if (ch === ')' || ch === ']') depth += 1;
    else if (ch === '(' || ch === '[') depth -= 1;
    else if (ch === '*' && depth === 0) return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
  }
  return null;
}
function splitFirstTimes(s) {
  let depth = 0;
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (ch === '(' || ch === '[') depth += 1;
    else if (ch === ')' || ch === ']') depth -= 1;
    else if (ch === '*' && depth === 0) return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
  }
  return null;
}
const unwrap = (s) => {
  const t = String(s || '').trim();
  if (!/^\(.*\)$/.test(t)) return t;
  let depth = 0;
  for (let i = 0; i < t.length; i += 1) {
    if (t[i] === '(') depth += 1;
    else if (t[i] === ')') { depth -= 1; if (depth === 0 && i < t.length - 1) return t; }
  }
  return t.slice(1, -1).trim();
};

/** A byte size → { count, type } ('' count = one), or null. */
function sizeParts(s) {
  const t = String(s || '').trim();
  const one = SIZEOF_RE.exec(t);
  if (one) return { count: '', type: cTypeOf(one[1]) };
  const last = splitTimes(t);
  if (last) {
    const m = SIZEOF_RE.exec(last[1]);
    if (m && last[0]) return { count: unwrap(last[0]), type: cTypeOf(m[1]) };
  }
  const first = splitFirstTimes(t);
  if (first) {
    const m = SIZEOF_RE.exec(first[0]);
    if (m && first[1]) return { count: unwrap(first[1]), type: cTypeOf(m[1]) };
  }
  return null;
}

/** Top-level comma split of a call's values (no strings expected here). */
function callArgs(inner) {
  const out = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < inner.length; i += 1) {
    const ch = inner[i];
    if (ch === '(' || ch === '[') depth += 1;
    else if (ch === ')' || ch === ']') depth -= 1;
    else if (ch === ',' && depth === 0) { out.push(inner.slice(start, i).trim()); start = i + 1; }
  }
  out.push(inner.slice(start).trim());
  return out.length === 1 && out[0] === '' ? [] : out;
}

/**
 * A stored memory job in parts, or null when it is not one the step can hold:
 *   { job: 'get', target, count, type, start: 'found' | 'zero', cast }
 *   { job: 'resize', target, count, type, cast }
 *   { job: 'free', target }
 * `cast` is the cast text an old step wrote (`(int *) `), kept when rewritten.
 */
export function parseMemory(expression, keep = '') {
  let s = String(expression || '').trim();
  const k = String(keep || '').trim();
  let cast = '';
  const c = CAST_RE.exec(s);
  if (c) { cast = c[0]; s = s.slice(c[0].length); }
  const m = /^(malloc|calloc|realloc|free)\s*\((.*)\)$/.exec(s);
  if (!m) return null;
  const args = callArgs(m[2]);
  if (m[1] === 'free') {
    if (args.length !== 1 || cast || k || !/^[A-Za-z_][\w.\->[\]]*$/.test(args[0])) return null;
    return { job: 'free', target: args[0] };
  }
  if (m[1] === 'malloc') {
    const p = args.length === 1 ? sizeParts(args[0]) : null;
    return p ? { job: 'get', target: k, count: p.count, type: p.type, start: 'found', cast } : null;
  }
  if (m[1] === 'calloc') {
    if (args.length !== 2) return null;
    const t = SIZEOF_RE.exec(args[1]);
    if (!t) return null;
    return { job: 'get', target: k, count: unwrap(args[0]) === '1' ? '' : unwrap(args[0]), type: cTypeOf(t[1]), start: 'zero', cast };
  }
  // realloc: kept straight back into the same pointer.
  if (args.length !== 2 || !k || args[0] !== k) return null;
  const p = sizeParts(args[1]);
  return p && p.count ? { job: 'resize', target: k, count: p.count, type: p.type, cast } : null;
}

/** A count that is more than one name or number goes in brackets before `* sizeof`. */
const times = (count) => {
  const t = String(count || '').trim();
  return /^[\w.]+$|^[\w.]+\[[^\]]*\]$/.test(t) ? t : `(${t})`;
};

/**
 * The C for a job's parts → { expression, keep }. The cast an old step had is
 * kept while it still names the same type; a new step writes none (C needs none).
 */
export function composeMemory({ job, target, count = '', type, start = 'found', cast = '' }) {
  const n = String(count || '').trim();
  const T = cTypeOf(type);
  const castFor = cast && CAST_RE.exec(cast) && cTypeOf(CAST_RE.exec(cast)[1]) === T ? cast : '';
  if (job === 'free') return { expression: `free(${target})`, keep: '' };
  if (job === 'resize') return { expression: `${castFor}realloc(${target}, ${times(n)} * sizeof(${T}))`, keep: target };
  let call;
  if (start === 'zero') call = `calloc(${n || '1'}, sizeof(${T}))`;
  else call = n ? `malloc(${times(n)} * sizeof(${T}))` : `malloc(sizeof(${T}))`;
  return { expression: `${castFor}${call}`, keep: target };
}

/** The old guided form's fields for the same job, so `?inputEditor=old` opens it guided. */
export function guidedFields(parts) {
  if (!parts) return {};
  if (parts.job === 'free') return { kind: 'free', free: { target: parts.target } };
  if (parts.job === 'resize') return { kind: 'resize', resize: { target: parts.target, count: parts.count } };
  return { kind: 'allocate', alloc: { target: parts.target, count: parts.count, fn: parts.start === 'zero' ? 'calloc' : 'malloc' } };
}

// ── which steps ──────────────────────────────────────────────────────────────

/** A Do step made as a Memory step, or holding one of its jobs (a guided form, a plain call). */
export function isMemoryStep(node) {
  if (!node || node.type !== 'action') return false;
  const ad = node.actionData || {};
  if (ad.stepKind === 'memory') return true;
  if (MEMORY_KINDS.includes(ad.kind)) return true;
  const keep = (ad.assignTo && ad.assignTo.fullPath) || '';
  return !!parseMemory(ad.expression || '', keep);
}

/**
 * Whether the Memory step can hold a stored step: an empty one, or one whose
 * C it reads exactly. Else Do (or the old editor) keeps it.
 */
export function memoryStepHolds(node) {
  if (!isMemoryStep(node)) return false;
  const ad = node.actionData || {};
  const expr = String(ad.expression || '').trim();
  if (!expr) return true;
  return !!parseMemory(expr, (ad.assignTo && ad.assignTo.fullPath) || '');
}

/** The parts a stored step holds now (a draft, a guided form, its C). */
export function storedMemory(node) {
  const ad = (node && node.actionData) || {};
  const keep = (ad.assignTo && ad.assignTo.fullPath) || '';
  const parsed = parseMemory(ad.expression || '', keep);
  if (parsed) return { ...parsed, complete: true };
  const d = ad.memoryDraft || {};
  if (d.job) return { job: d.job, target: d.target || '', count: d.count || '', type: d.type || '', start: d.start || 'found', cast: '', complete: false };
  if (ad.kind === 'allocate' && ad.alloc) return { job: 'get', target: ad.alloc.target || '', count: ad.alloc.count || '', type: '', start: ad.alloc.fn === 'malloc' ? 'found' : 'zero', cast: '', complete: false };
  if (ad.kind === 'resize' && ad.resize) return { job: 'resize', target: ad.resize.target || '', count: ad.resize.count || '', type: '', cast: '', complete: false };
  if (ad.kind === 'free' && ad.free) return { job: 'free', target: ad.free.target || '', complete: false };
  return { job: '', target: '', count: '', type: '', start: 'found', cast: '', complete: false };
}

// ── words ────────────────────────────────────────────────────────────────────

const howMany = (count, type) => {
  const n = String(count || '').trim();
  return n ? `${n} ${kindWords(type, n !== '1')}` : `one ${kindWords(type, false)}`;
};

/** What the canvas card and Code Next say: "Get memory for n whole numbers, every value 0 → p". */
export function memoryWords(parts) {
  if (!parts || !parts.job) return '';
  if (parts.job === 'free') return parts.target ? `Give back ${parts.target}'s memory` : 'Give memory back';
  if (parts.job === 'resize') return `Resize ${parts.target || '…'}'s memory to ${howMany(parts.count, parts.type)}`;
  const zero = parts.start === 'zero' ? ', every value 0' : '';
  return `Get memory for ${howMany(parts.count, parts.type)}${zero}${parts.target ? ` → ${parts.target}` : ''}`;
}

/** Code Next's note: which C does it, and what sizeof means. */
export function memoryCNote(parts) {
  if (!parts || !parts.job) return '';
  if (parts.job === 'free') return 'in C: free, from stdlib.h';
  const fn = parts.job === 'resize' ? 'realloc' : (parts.start === 'zero' ? 'calloc' : 'malloc');
  return `in C: ${fn}, from stdlib.h · the size of one ${kindWords(parts.type, false)} is sizeof(${cTypeOf(parts.type)})`;
}

// ── the right step for the job ──────────────────────────────────────────────

/**
 * A memory job written into another step's value (a Set): refused in plain
 * words — the right step for the job is Memory. null when there is none.
 */
export function memoryJobRefusal(expr) {
  const m = /\b(malloc|calloc|realloc|free)\s*\(/.exec(String(expr || ''));
  if (!m) return null;
  if (m[1] === 'free') return 'Giving memory back is its own step — use Memory → Give memory back.';
  if (m[1] === 'realloc') return 'Resizing memory is its own step — use Memory → Resize memory.';
  return 'Getting memory is its own step — use Memory → Get memory.';
}

// ── notes: NULL, never given back, used after, given back twice ─────────────

const CHILD_LISTS = (n) => [
  ...((n && n.outcomes) || []).map((o) => (o && o.nodes) || []),
  (n && n.loopBody) || [], (n && n.afterLast) || [],
  ...((n && n.branches) || []).map((b) => (b && b.nodes) || []),
  (n && n.tryBody) || [], ...(((n && n.catchBlocks) || []).map((c) => (c && c.nodes) || [])), (n && n.finallyBody) || [],
];

function walk(list, fn) {
  (list || []).forEach((n) => { if (!n) return; fn(n); CHILD_LISTS(n).forEach((l) => walk(l, fn)); });
}

/** The list a step sits in, and where. */
function placeOf(list, id) {
  for (let i = 0; i < (list || []).length; i += 1) {
    const n = list[i];
    if (!n) continue; // eslint-disable-line no-continue
    if (n.id === id) return { list, index: i };
    const lists = CHILD_LISTS(n);
    for (let j = 0; j < lists.length; j += 1) {
      const hit = placeOf(lists[j], id);
      if (hit) return hit;
    }
  }
  return null;
}

const SKIP_KEYS = new Set(['id', 'type', 'label', 'description', 'validationErrorMessage', 'caseName', 'hasError', 'hasValidationErrors', 'stepKind', 'kind', 'memoryDraft', 'alloc', 'free', 'resize']);

/** Every value a step's own fields write (not its children's), with resource ids as names. */
function ownTexts(n, idToName) {
  const out = [];
  const visit = (v, key) => {
    if (SKIP_KEYS.has(key)) return;
    if (['outcomes', 'loopBody', 'afterLast', 'branches', 'tryBody', 'catchBlocks', 'finallyBody', 'nodes'].includes(key)) return;
    if (typeof v === 'string') {
      const base = v.replace(/\[.*$/, '');
      out.push(idToName.has(base) ? idToName.get(base) + v.slice(base.length) : v);
    } else if (Array.isArray(v)) v.forEach((x) => visit(x, ''));
    else if (v && typeof v === 'object') Object.entries(v).forEach(([k, x]) => visit(x, k));
  };
  Object.entries(n || {}).forEach(([k, v]) => visit(v, k));
  return out;
}

const mentions = (texts, name) => {
  const re = new RegExp(`(^|[^\\w.])${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w])`);
  return texts.some((t) => re.test(t));
};

/** Does a step (or anything inside it) use `name`? */
function stepUses(n, name, idToName) {
  let hit = false;
  walk([n], (x) => { if (!hit && mentions(ownTexts(x, idToName), name)) hit = true; });
  return hit;
}

/**
 * The notes a complete Memory step shows → [['info' | 'warn', text]].
 *   flowNodes   the flow's steps; params its parameter names; resources (for ids → names)
 */
export function memoryNotes(node, { flowNodes = [], params = [], resources = [] } = {}) {
  const ad = (node && node.actionData) || {};
  const parts = parseMemory(ad.expression || '', (ad.assignTo && ad.assignTo.fullPath) || '');
  if (!parts || !parts.target) return [];
  const p = parts.target;
  const idToName = new Map((resources || []).filter((r) => r && r.id && r.name).map((r) => [r.id, r.name]));
  const steps = [];
  walk(flowNodes, (n) => { if (n && n.type === 'action') steps.push(n); });
  const memOf = (n) => parseMemory((n.actionData || {}).expression || '', ((n.actionData || {}).assignTo || {}).fullPath || '');
  const frees = steps.filter((n) => { const m = memOf(n); return m && m.job === 'free' && m.target === p; });
  const gets = steps.filter((n) => { const m = memOf(n); return m && (m.job === 'get' || m.job === 'resize') && m.target === p; });
  const notes = [];

  if (parts.job === 'get' || parts.job === 'resize') {
    notes.push(['info', parts.job === 'get'
      ? `If no memory is free, ${p} is NULL — check it with a Decision next (${p} is not NULL).`
      : `If there is not enough memory to grow, ${p} is NULL — check it with a Decision next (${p} is not NULL).`]);
    if (parts.job === 'get' && !frees.length) {
      // Handed on (given back by a return, or kept somewhere else) is not forgotten.
      let handedOn = false;
      walk(flowNodes, (n) => {
        if (handedOn || !n) return;
        if (n.type === 'return' && mentions(ownTexts(n, idToName), p)) handedOn = true;
        if (n.type === 'assignment' && (n.assignments || []).some((a) => String((a && (a.expression || a.directValue || a.sourceFullPath)) || '').trim() === p)) handedOn = true;
        if (n.type === 'subflow' && mentions(ownTexts(n, idToName), p)) handedOn = true;
      });
      if (!handedOn) notes.push(['warn', `${p} is never given back in this flow — add a Memory step after its last use: Give memory back → ${p}.`]);
    }
  }

  if (parts.job === 'free') {
    const madeHere = gets.length || params.includes(p);
    let setHere = false;
    walk(flowNodes, (n) => {
      if (setHere || !n) return;
      if (n.type === 'assignment' && (n.assignments || []).some((a) => String((a && a.fullPath) || '').trim() === p)) setHere = true;
      if (n.type === 'action' && n.id !== node.id && String(((n.actionData || {}).assignTo || {}).fullPath || '').trim() === p) setHere = true;
    });
    if (!madeHere && !setHere) notes.push(['warn', `${p} is never given memory in this flow — add a Memory step before this one: Get memory → ${p}.`]);
    if (frees.length > 1) notes.push(['warn', `${p} is given back in ${frees.length} steps — giving the same memory back twice stops the program. Make sure only one of them runs.`]);
    // Used after it is given back: a later step in the same list, before it gets memory again.
    const at = placeOf(flowNodes, node.id);
    if (at) {
      for (let i = at.index + 1; i < at.list.length; i += 1) {
        const n = at.list[i];
        const m = n && n.type === 'action' ? memOf(n) : null;
        if (m && m.job === 'get' && m.target === p) break;
        if (n && n.type !== 'end' && stepUses(n, p, idToName)) {
          const what = String(n.label || n.description || n.type).trim();
          notes.push(['warn', `${p} is used after it is given back (${what}) — that memory is no longer yours. Give it back after its last use.`]);
          break;
        }
      }
    }
  }
  return notes;
}
